import { Component, ErrorInfo, ReactNode } from "react";
import { claimRecoveryAttempt } from "@/utils/loadingRecovery";

interface Props { children: ReactNode }
interface State { hasError: boolean; message: string }

const RELOAD_FLAG = "gps_dom_error_reload_attempted";

// "Failed to execute 'removeChild'/'insertBefore' on 'Node'" means React's
// virtual DOM tree and the real DOM disagreed about what's there - same
// family of bug as the chunk-mismatch #321 error lazyWithReload handles
// (src/utils/lazyWithReload.ts), just surfacing at the DOM-commit step
// instead of at import(). A stale tab running an old bundle, or a browser
// extension (translate, ad blockers) mutating nodes React owns, both land
// here. There's no way to recover render state once this fires, but the
// error is transient per-load, so one automatic reload (capped like
// lazyWithReload, so a genuinely broken deploy doesn't reload-loop) clears
// it for the user instead of stranding them on the dead-end screen.
//
// React #321 ("Invalid hook call") is the same stale-bundle-vs-fresh-chunk
// mismatch, but it doesn't always go through lazyWithReload's import()
// catch: a chunk can fetch successfully yet still be incompatible with the
// React instance already running in memory, and that throws synchronously
// during render/hook execution instead of at import() time - landing here
// instead. Without matching this message too, those cases fell through to
// the dead-end "Algo deu errado" screen instead of self-healing.
function isStaleBundleError(error: Error): boolean {
  if (/removeChild|insertBefore|appendChild/.test(error.message) && /Node/.test(error.message)) {
    return true;
  }
  return /Minified React error #321|invariant=321|Invalid hook call/.test(error.message);
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Full stack + component stack logged for diagnosis; the UI only shows
    // the short message since minified prod errors (e.g. React #321) are
    // meaningless to end users without this context.
    console.error("[AppErrorBoundary]", error, error.stack, errorInfo.componentStack);

    if (isStaleBundleError(error) && claimRecoveryAttempt(RELOAD_FLAG)) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-8 text-center">
          <h1 className="text-3xl font-bold text-red-600 mb-3">Algo deu errado</h1>
          <p className="text-gray-600 mb-2">Não foi possível carregar esta página.</p>
          {this.state.message && (
            <p className="text-sm text-gray-400 mb-6 font-mono">{this.state.message}</p>
          )}
          <a href="/" className="text-blue-600 hover:underline font-medium">
            Voltar para a página inicial
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}
